/**
 * Pure decision/parsing logic for team-chat channel realtime events.
 *
 * The Socket.IO server only delivers `message.*` events to the room
 * `channel:<id>`, and sockets are auto-joined to their EXISTING memberships
 * on connect. Channels created (or joined) while the socket is already
 * connected therefore need an explicit client→server `join_channel` emit —
 * the RealtimeManager owns the socket plumbing; the join/leave DECISIONS,
 * typing-event parsing and typer expiry live here so they stay unit-testable
 * without React or a socket. Pattern mirrors `slash-commands.ts` /
 * `scroll-stick.ts`.
 */

import { formatRelativeDate, getLocaleSnapshot, translate } from '@/lib/i18n/runtime';
import type { Locale } from '@/lib/i18n/locales';

/** Loosely-typed WS payload — different backend builds flatten differently. */
export type WSEventData = Record<string, unknown> | undefined;

function pickString(v: unknown): string | undefined {
  return typeof v === 'string' && v.length > 0 ? v : undefined;
}

function pickStringArray(v: unknown): string[] | undefined {
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : undefined;
}

/**
 * `channel.member_added` → should THIS client emit `join_channel`?
 *
 * True when the payload names me (via `user_ids: string[]` or a single
 * `user_id`). When we CAN'T determine membership — payload carries no user
 * pointer, or we don't know our own id yet — we return true and emit anyway:
 * the backend validates membership server-side, so a spurious join is a
 * cheap, idempotent no-op, while a missed join means silent message loss.
 */
export function shouldJoinOnMemberAdded(data: WSEventData, myUserId: string | undefined): boolean {
  if (!myUserId) return true;
  const single = pickString(data?.user_id);
  const many = pickStringArray(data?.user_ids);
  if (single === undefined && many === undefined) return true;
  if (single === myUserId) return true;
  return many?.includes(myUserId) ?? false;
}

/**
 * `channel.member_removed` → was it ME who got removed? Only then should the
 * client leave the Socket.IO room and drop the messages cache. Unlike the
 * join decision this must be STRICT: leaving a room we still belong to (or
 * nuking the cache) on someone else's removal is the harmful direction, so
 * "can't determine" → false.
 */
export function isSelfMemberRemoved(data: WSEventData, myUserId: string | undefined): boolean {
  if (!myUserId) return false;
  const single = pickString(data?.user_id);
  const many = pickStringArray(data?.user_ids);
  return single === myUserId || (many?.includes(myUserId) ?? false);
}

/**
 * The generic typing store is keyed by conversation id; channel typers reuse
 * the exact same map/TTL/sweep mechanics under a namespaced key so the two
 * lanes can never collide (a channel id and a conversation id are both UUIDs).
 */
export const CHANNEL_TYPING_PREFIX = 'channel:';

export function channelTypingKey(channelId: string): string {
  return CHANNEL_TYPING_PREFIX + channelId;
}

export interface ChannelTypingEvent {
  channelId: string;
  userId: string;
  userName: string;
}

/**
 * Parse the server-enriched channel `typing` broadcast
 * (`{ channel_id, user_id, user_name }`, sometimes flattened to the envelope
 * top level). Returns null when the event is unusable (no channel/user) or is
 * our own echo — the backend excludes the emitter from the room broadcast,
 * but we drop self defensively like the inbox `inbox:typing` handler does.
 */
export function parseChannelTypingEvent(
  data: WSEventData,
  envelope: Record<string, unknown>,
  selfUserId: string | undefined,
): ChannelTypingEvent | null {
  const channelId = pickString(data?.channel_id) ?? pickString(envelope.channel_id);
  const userId = pickString(data?.user_id) ?? pickString(envelope.user_id);
  if (!channelId || !userId) return null;
  if (selfUserId && userId === selfUserId) return null;
  const userName =
    pickString(data?.user_name) ?? pickString(envelope.user_name) ?? 'Alguien';
  return { channelId, userId, userName };
}

/** Desktop-notification body cap for mentions (mirrors maybeNotifyOnNewMessage's 120). */
export const MENTION_BODY_MAX = 120;

export interface MentionNotification {
  title: string;
  body: string;
  /** Literal identity, independent of the translated title. */
  senderName: string;
  /** Dedup tag — prefer the message id so distinct mentions each surface. */
  tag: string | undefined;
}

/**
 * Build the `message.mention` desktop notification
 * ({ message_id, channel_id, user_id, user_name, content } → title/body/tag).
 * Truncates the content with an ellipsis past MENTION_BODY_MAX.
 */
export function buildMentionNotification(data: WSEventData, locale: Locale = getLocaleSnapshot().locale): MentionNotification {
  const userName = pickString(data?.user_name) ?? translate('notifications.unknownSender', {}, 'common', locale);
  const raw = pickString(data?.content) ?? '';
  // Count code points and stop at the cap; never split a UTF-16 surrogate pair
  // or allocate a full copy of a long customer message just for the preview.
  const points: string[] = [];
  for (const point of raw) {
    if (points.length === MENTION_BODY_MAX) { points[MENTION_BODY_MAX - 1] = '…'; break; }
    points.push(point);
  }
  const body = points.join('');
  return {
    title: translate('notifications.mentionTitle', { name: userName }, 'common', locale),
    body,
    senderName: userName,
    tag: pickString(data?.message_id) ?? pickString(data?.channel_id),
  };
}

export function buildThreadNotification(
  type: 'thread:assigned' | 'thread:due_soon', data: WSEventData,
  locale: Locale = getLocaleSnapshot().locale, now = Date.now(),
) {
  const text = (key: string, params: Record<string, string> = {}) => translate(`notifications.${key}`, params, 'common', locale);
  const title = pickString(data?.title)?.trim() || text('threadTitle');
  const name = pickString(data?.assigned_by_name);
  const dueAt = pickString(data?.due_at);
  return {
    title: text(type === 'thread:assigned' ? 'threadAssigned' : 'threadDue', { title }),
    body: type === 'thread:assigned'
      ? name ? text('threadBy', { name }) : ''
      : dueAt && Number.isFinite(Date.parse(dueAt)) ? formatRelativeDate(dueAt, locale, now) : '',
  };
}

/**
 * Filter out expired typers and sort most-recently-active first. Pure so the
 * TTL semantics are testable; the store's 1s `clearStale` sweep remains the
 * mechanism that eventually deletes the entries (and bumps `tick`).
 */
export function activeTypers<T extends { expiresAt: number }>(entries: T[], now: number): T[] {
  return entries.filter((e) => e.expiresAt > now).sort((a, b) => b.expiresAt - a.expiresAt);
}
